// Feature: features/nma-meta-analysis-report-page.md
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  Select,
  Divider,
  Alert,
  AlertIcon,
  List,
  ListItem,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { ExternalLinkIcon, CalendarIcon, WarningTwoIcon } from '@chakra-ui/icons';
import HeaderMenus from './HeaderMenus';

const META_ANALYSIS_EDITIONS = [
  {
    id: '2026-10-21',
    dateLabel: 'October 21, 2026',
    title: 'NMA Claims & Remittance Review — Initial Findings & Potential Opportunities (Beta)',
    intro:
      'This report summarizes initial findings from our new claims and remittance pipeline across 6,317 cases (7,992 Professional and Technical claims) reconciliating 837 submissions, 277CA clearinghouse responses, 835/EOB remittances, and Lockbox correspondence.',
    betaNotes: [
      'Beta Notice: The claims analytics pipeline is currently in validation. Data mappings, split-line reconciliations, and aggregate totals are still being verified.',
      'Scope Note: POS 15 (Mobile Unit) on Technical claims is recognized as an intentional workflow to avoid immediate front-end rejections.',
    ],
    sections: [
      {
        number: 1,
        title: 'Unlisted CPT 95999 (Train of Four) Rules & Carve-Outs',
        tag: 'Rules & Carve-Outs',
        tagColor: 'purple',
        observations: [
          'Across adjudicated claims in the current dataset, CPT 95999 ("NOC TRAIN OF FOUR") is denied on 90.8% of lines—primarily under NCCI bundling edits (CO-97 / PI-234) or 1-unit daily frequency limits (CO-222 / N362).',
          'On commercial payers such as UHC, UMR, and Aetna, billing unlisted code 95999 on a Professional claim frequently triggers an automated whole-claim medical record hold (CO-252 / PI-252 / M127), delaying adjudication on the companion IONM codes.',
          'At the same time, three specific payer segments show consistent reimbursement on 95999: Intermountain Healthcare plans (82.6% paid), Workers’ Comp & Auto (81.3% paid), and Surest / UHC Kelsey Seybold (69.6% paid).',
        ],
        potentialActions: [
          'Continue expanding remove_95999 rules for payers that consistently bundle or hold claims over 95999 (including Humana Medicare Advantage, UHC Commercial, UMR, and Aetna), while preserving carve-outs for Intermountain, Workers’ Comp, Surest, and Kelsey Seybold.',
          'Discontinue routine appeals on standalone 95999 bundling denials where payer policy explicitly excludes Train of Four.',
        ],
        links: [
          { label: 'Visual Stage Discovery — CPT 95999 (Tab 2)', path: '/nma-claims?tab=visual&cpt=95999', color: 'purple' },
          { label: 'Explore 95999 Claims (Tab 3)', path: '/nma-claims?tab=explorer&cpt=95999', color: 'blue' },
          { label: 'CPT Rules Manager (/cpt-rules)', path: '/cpt-rules', color: 'teal' },
        ],
      },
      {
        number: 2,
        title: 'Repeat Baseline SSEP (95938-76) — Payer MUE Edits vs. Dual-Line Reimbursement',
        tag: 'Modifier -76 & Rule #325',
        tagColor: 'purple',
        observations: [
          'When a repeat baseline study (95938-26-76) is billed alongside the base study (95938-26), certain Medicare and Medicare Advantage plans apply a strict 1-unit daily limit (MAI = 3) that denies both 95938 lines on the claim.',
          'This pattern appears on HealthSpring Medicare Advantage (38 claims), UHC/AARP Medicare Advantage (99 claims), WellMed (19 claims), and Medicare WA (11 claims), where both 95938 lines were denied (CO-273 / CO-151) even though the other CPT codes on the claim (95822, 95861, 95939) were paid on 114 of those same claims.',
          'Conversely, Humana Medicare Advantage paid both 95938-26 and 95938-26-76 on 216 of 228 Pro claims (94.7%). Currently, the general MEDICARE ADVANTAGE PLAN -> remove_baseline_studies rule (Rule #325) suppresses -76 across all MA plans, including Humana MA.',
          'On Optum VACCN (47 lines denied CO-16/M53) and Cigna (64 Pro lines denied PI-119/N362), the base 95938-26 line is paid while the repeat -76 line is consistently denied.',
        ],
        potentialActions: [
          'Refine Rule #325 (remove_baseline_studies) into a Dynamic Rule that carves out Humana MA and Aetna MA so dual lines continue to be billed where covered, while applying carrier-level removal on HealthSpring, UHC/AARP, WellMed, Traditional Medicare, Optum VACCN, and Cigna.',
          'Evaluate submitting corrected claims (billing only the single base 95938-26 line) on the 114 adjudicated Medicare/MA claims where companion CPTs were paid.',
        ],
        links: [
          { label: 'Visual Stage Discovery — CPT 95938 (Tab 2)', path: '/nma-claims?tab=visual&cpt=95938', color: 'purple' },
          { label: 'Explore 95938 Claims (Tab 3)', path: '/nma-claims?tab=explorer&cpt=95938', color: 'blue' },
          { label: 'CPT Rules Manager (/cpt-rules)', path: '/cpt-rules', color: 'teal' },
        ],
      },
      {
        number: 3,
        title: 'Continuous Monitoring Time Codes (95941 vs. G0453) & Daily Unit Caps',
        tag: 'Time Code Rules',
        tagColor: 'blue',
        observations: [
          'Across Professional claims in the dataset, CPT 95941 (per hour) was paid or allowed on 21.5% of adjudicated lines (137 of 636), compared to 58.3% on G0453 (and 68%–100% on UHC, Surest, Culinary, and HPN/Sierra).',
          'On Commercial Aetna (POS/EPO/PPO), 95941 was paid on 8.3% of lines (9 of 108), with most denials citing CO-56. Aetna CPB 0697 lists G0453 as the covered remote monitoring code and requires an approvable primary time code for companion modalities (95938/95939).',
          'On Community Health Choice HMO Plan, 18 of 19 claims with a denied 95941 line paid all other CPT codes on the claim. All 5 paid 95941 lines were billed at 1 unit ($828 allowed), whereas all 14 claims billed with 2 or more units denied for exceeding daily unit limits (CO-95 / N362). Recent rules (#356/#357) matched specific Community Health Choice carrier strings and capped at 2 units, leaving "COMMUNITY HEALTH CHOICE HMO PLAN" uncapped.',
          'On HPN/Sierra Health & Life HMO (6 claims) and UMR (5 claims where "Private Insurance" payer type bypassed the payer-level UMR rule), 95941 denied while G0453 was reimbursed.',
        ],
        potentialActions: [
          'Add change_95941_to_G0453 for Commercial Aetna and ensure carrier-level coverage for UMR and HPN/Sierra.',
          'Update the Community Health Choice rule to match carrier CONTAINS "COMMUNITY HEALTH CHOICE" and evaluate a 1-unit cap (along with rebilling the 14 N362 claims at 1 unit).',
        ],
        links: [
          { label: 'CPT Rules Manager (/cpt-rules)', path: '/cpt-rules', color: 'blue' },
          { label: 'Visual Stage Discovery — CPT 95941 (Tab 2)', path: '/nma-claims?tab=visual&cpt=95941', color: 'purple' },
          { label: 'Explore 95941 Claims (Tab 3)', path: '/nma-claims?tab=explorer&cpt=95941', color: 'teal' },
        ],
      },
      {
        number: 4,
        title: 'Rendering Provider NPI / Taxonomy Rejections (N290) & Clearinghouse Enrollment Holds',
        tag: 'Provider Enrollment & 837 Configuration',
        tagColor: 'orange',
        observations: [
          '81 Professional claims (331 non-95999 lines) were rejected with CO-16 and remark N290 ("Missing/incomplete/invalid rendering provider primary identifier — resubmit with corrected information") or CO-109, concentrated in five physician-payer combinations:',
          '• Dr. Timothy Jiang (NPI 1447879440) on Medicare S CA (25 Pro claims) and Dr. Cody Nathan (NPI 1336671338) on Medicare TX (10 Pro claims). On several of these 837 files, Loop 2310C (Service Facility) also contains the physician’s name and NPI rather than the hospital facility.',
          '• Dr. Danny M. Chachere (NPI 1982994448) on Michigan BCBS / Medicare Plus Blue (21 Pro claims).',
          '• Dr. Kevin Mochizuki (NPI 1871679787, 11 denied claims in RI; 190 Pro claims total) and Dr. Megan Hildner (17 Pro claims) have taxonomy 231H00000X (Audiologist) populated on the outbound 837 instead of Neurology / Clinical Neurophysiology (2084N0400X / 2084N0600X).',
          'At the 277CA clearinghouse stage, four enrollment items account for 143 rejected Pro claims: BCBS IL PPO (79 claims: NPI 1770111452 / TIN 85-0542512), Medicare PA (28 claims: submitter EDI approval), BCBS MA (22 claims: 2100B eligibility), and Medicaid NV (14 claims: 2100C enrollment dates).',
        ],
        potentialActions: [
          'Update the outbound 837 provider taxonomy for Drs. Mochizuki and Hildner from 231H00000X (Audiologist) to Neurology/Neurophysiology, and verify that Loop 2310C consistently maps the hospital facility NPI.',
          'Review PECOS / payer roster links for those physician-payer pairs and the four clearinghouse enrollment setups so affected Pro claims can be resubmitted.',
        ],
        links: [
          { label: '277CA Clearinghouse Rejections (Tab 3)', path: '/nma-claims?tab=explorer&quick=CH_REJECTED', color: 'orange' },
          { label: 'Explore Medicare S CA Claims (Tab 3)', path: '/nma-claims?tab=explorer&payer=MEDICARE%20S%20CA', color: 'blue' },
        ],
      },
      {
        number: 5,
        title: 'Intraoperative EEG Code Preferences (95822 vs. 95955) & NCCI XU Modifier Rules',
        tag: 'EEG & NCCI Rules',
        tagColor: 'teal',
        observations: [
          'Adjudication rates between EEG codes 95822 and 95955 vary significantly by payer:',
          '• On Aetna (77.5% vs. 6.0%) and Cigna (77.8% vs. 15.2%), 95822 is reimbursed at a much higher rate than 95955.',
          '• On BCBS (65.0% vs. 49.5%) and Ambetter (68.6% vs. 50.0%), 95955 is reimbursed at a higher rate than 95822.',
          '• On UHC and UHC/AARP Medicare Advantage, 95822 is denied on 63.3% of spine claims (PI-16/M51 and CO-16/M50) even when companion CPTs on the claim are paid, reflecting UHC’s policy limiting intraoperative EEG to intracranial/vascular procedures.',
          'Separately, four NCCI modifier rules added on Sept 13 (#345 apply_xu_51785_95940, #346 apply_xu_51785_G0453, #347 apply_xu_95955_95940, #348 apply_xu_95955_G0453) have the code order inverted—attaching modifier XU to the Column 1 code (51785/95955) rather than the Column 2 code (95940/G0453).',
        ],
        potentialActions: [
          'Configure use_95822_for_eeg for Aetna and Cigna while retaining 95955 for BCBS and Ambetter, and evaluate suppressing EEG on routine non-cranial UHC spine cases.',
          'Reverse the code order on Rules #345–#348 in /cpt-rules so modifier XU is applied to the Column 2 target (95940 and G0453).',
        ],
        links: [
          { label: 'CPT Rules Manager (/cpt-rules)', path: '/cpt-rules', color: 'blue' },
          { label: 'Visual Stage Discovery — CPT 95822 (Tab 2)', path: '/nma-claims?tab=visual&cpt=95822', color: 'teal' },
        ],
      },
      {
        number: 6,
        title: 'Pending Medical Record Requests (325 Unsent Pro Claims) & Professional POS Mismatches',
        tag: 'Medical Records & Pro POS',
        tagColor: 'orange',
        observations: [
          'There are currently 479 zero-pay Professional claims in Pending Medical Records status, of which 325 do not yet have a recorded RECORDS_SENT milestone (146 on UHC/AARP MA, 79 on UHC Commercial POS/EPO, 44 on Cigna, 28 on Aetna POS, and 9 unsent + 37 sent on UMR).',
          'On UMR, Lockbox correspondence shows two separate fax destinations requested per claim: clinical IONM records to 877-292-0420 and ordering/referring physician documentation to 855-444-2896.',
          'Separately, 9 UHC Commercial Professional claims (P279398, P285546, P286633, P287964, P288006, P290807, P293301, P294009, P294600) were denied (PI-171 / Code QH) because Outpatient POS 22 or ASC POS 24 was billed on the Pro claim when the hospital face sheet indicated Inpatient POS 21 (or vice versa).',
        ],
        potentialActions: [
          'Prioritize medical record submissions on the 325 unsent Pro requests—starting with Inpatient (POS 21) Aetna and UHC claims—and verify that UMR submissions include the ordering physician order to Fax 855-444-2896.',
          'Review the 9 UHC Professional POS mismatch claims for corrected claim resubmission (POS 21 vs. 22).',
        ],
        links: [
          { label: 'Medical Records Tracker (Tab 4)', path: '/nma-claims?tab=records', color: 'orange' },
          { label: 'Pending Records Worklist (Tab 3)', path: '/nma-claims?tab=explorer&quick=RECORDS_NEEDED', color: 'purple' },
        ],
      },
      {
        number: 7,
        title: 'Technical POS 15 / POS 49 Workflow Refinements',
        tag: 'Technical Claim Workflow',
        tagColor: 'cyan',
        observations: [
          'Billing POS 15 (Mobile Unit) on commercial Technical claims and POS 49 (Clinic) on Medicare/MA Technical claims achieves a 97.3% clearinghouse acceptance rate, generating allowed/paid claims on Surest POS, out-of-network BCBS PPO plans (IN, CO, GA, PA), Cigna MultiPlan wraps, Medicare TX (62.8% paid), and Humana MA PPO (70.5% paid).',
          'On BCBS AR PPO, 7 Inpatient Technical claims billed with POS 15 rejected at the 277CA clearinghouse stage with error 090.001.828.626195: "The admission date (2300 DTP) is used. It should only be used when POS (2300 CLM05-01) is Inpatient."',
          'On Cigna Technical claims, billing physician-only code 95940 (pctc_ind = 0) alongside -TC study codes triggers additional modifier and bundling edits.',
          'Two plans in the dataset reimbursed Technical claims at a higher rate when billed with Facility POS 21/22 rather than POS 15/49: Oscar Health Plan (85.7% of -TC lines paid in POS 21/22 vs. 37.5% in POS 15) and Humana Medicare HMO Replacement (where T290098 and T292589 denied POS 49 for clinic prior authorization).',
        ],
        potentialActions: [
          'When billing POS 15 on an Inpatient case, omit the Inpatient Admission Date segment (Loop 2300 DTP*435) on the outbound 837 to prevent clearinghouse syntax rejections.',
          'Evaluate adding remove_95940 on Cigna Technical claims (consistent with existing UHC Rule #234), and using Facility POS 21/22 for Oscar Health Plan and Humana HMO Replacement.',
        ],
        links: [
          { label: 'Visual Stage Discovery — All Claims (Tab 2)', path: '/nma-claims?tab=visual&cpt=ALL_CLAIMS', color: 'blue' },
          { label: 'Explore Claims Worklist (Tab 3)', path: '/nma-claims?tab=explorer', color: 'purple' },
        ],
      },
    ],
  },
];

const NMAMetaAnalysisReport = () => {
  const navigate = useNavigate();
  const [selectedEditionId, setSelectedEditionId] = useState(META_ANALYSIS_EDITIONS[0].id);

  const edition =
    META_ANALYSIS_EDITIONS.find((e) => e.id === selectedEditionId) || META_ANALYSIS_EDITIONS[0];

  return (
    <Box minH="100vh" bg="gray.50" pb={16}>
      <HeaderMenus />

      {/* Prominent Red BETA Banner */}
      <Box bg="red.600" color="white" py={2.5} px={6} shadow="sm">
        <Flex maxW="960px" mx="auto" align="center" justify="center" gap={3} wrap="wrap" textAlign="center">
          <Badge bg="white" color="red.700" fontWeight="extrabold" fontSize="xs" px={2.5} py={0.5} borderRadius="sm">
            BETA
          </Badge>
          <HStack spacing={2}>
            <WarningTwoIcon boxSize={4} />
            <Text fontWeight="bold" fontSize="sm" letterSpacing="wide">
              Beta — Pipeline &amp; analytics in validation. Findings and figures below are still being verified.
            </Text>
          </HStack>
        </Flex>
      </Box>

      <Box maxW="960px" mx="auto" pt={8} px={4}>
        {/* Main Document Container */}
        <Box
          bg="white"
          p={{ base: 6, md: 10 }}
          borderRadius="lg"
          boxShadow="sm"
          border="1px solid"
          borderColor="gray.200"
        >
          {/* Top Bar: Date Badge + Edition Selector */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={4}>
            <HStack spacing={2}>
              <Badge colorScheme="purple" variant="solid" px={3} py={1} borderRadius="md" fontSize="xs">
                <CalendarIcon mr={1.5} />
                {edition.dateLabel}
              </Badge>
              <Badge colorScheme="orange" variant="subtle" px={2.5} py={1} fontSize="xs">
                BETA — INITIAL REVIEW
              </Badge>
            </HStack>

            <HStack spacing={2}>
              <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase">
                Report Date:
              </Text>
              <Select
                size="sm"
                w="210px"
                bg="gray.50"
                fontWeight="semibold"
                value={selectedEditionId}
                onChange={(e) => setSelectedEditionId(e.target.value)}
              >
                {META_ANALYSIS_EDITIONS.map((ed) => (
                  <option key={ed.id} value={ed.id}>
                    {ed.dateLabel}
                  </option>
                ))}
              </Select>
            </HStack>
          </Flex>

          {/* Title & Intro */}
          <Heading as="h1" size="lg" color="gray.800" mb={3}>
            {edition.title}
          </Heading>
          <Text fontSize="md" color="gray.600" lineHeight="1.7" mb={5}>
            {edition.intro}
          </Text>

          {/* Quick Navigation Links */}
          <Flex wrap="wrap" gap={2} mb={6}>
            <Button
              size="xs"
              colorScheme="blue"
              rightIcon={<ExternalLinkIcon />}
              onClick={() => navigate('/nma-claims')}
            >
              NMA Claims Dashboard (/nma-claims)
            </Button>
            <Button
              size="xs"
              colorScheme="purple"
              variant="outline"
              rightIcon={<ExternalLinkIcon />}
              onClick={() => navigate('/nma-claims?tab=visual')}
            >
              Visual Stage Discovery (Tab 2)
            </Button>
            <Button
              size="xs"
              colorScheme="teal"
              variant="outline"
              rightIcon={<ExternalLinkIcon />}
              onClick={() => navigate('/cpt-rules')}
            >
              CPT Rules Manager (/cpt-rules)
            </Button>
            <Button
              size="xs"
              colorScheme="gray"
              variant="outline"
              rightIcon={<ExternalLinkIcon />}
              onClick={() => navigate('/jobs')}
            >
              Analysis Jobs (/jobs)
            </Button>
          </Flex>

          {/* Beta Context Note */}
          <Alert
            status="warning"
            variant="left-accent"
            borderRadius="md"
            bg="orange.50"
            p={4}
            mb={8}
            flexDirection="column"
            alignItems="flex-start"
          >
            <HStack mb={1}>
              <AlertIcon />
              <Text fontWeight="bold" fontSize="sm" color="orange.900">
                Validation &amp; Scope Notes:
              </Text>
            </HStack>
            <List spacing={1.5} pl={6} styleType="disc" fontSize="sm" color="orange.900">
              {edition.betaNotes.map((note, idx) => (
                <ListItem key={idx}>{note}</ListItem>
              ))}
            </List>
          </Alert>

          <Divider mb={8} />

          {/* Clean Narrative Sections */}
          <VStack spacing={10} align="stretch">
            {edition.sections.map((sec, idx) => (
              <Box key={sec.number}>
                <HStack spacing={2} mb={2}>
                  <Badge colorScheme={sec.tagColor} fontSize="xs" px={2.5} py={0.5} borderRadius="sm">
                    Section {sec.number}
                  </Badge>
                  <Badge variant="outline" colorScheme={sec.tagColor} fontSize="xs">
                    {sec.tag}
                  </Badge>
                </HStack>

                <Heading as="h2" size="md" color="gray.800" mb={4}>
                  {sec.number}. {sec.title}
                </Heading>

                {/* Data Observations */}
                <Box mb={4}>
                  <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2}>
                    Data Observations:
                  </Text>
                  <List spacing={2} pl={5} styleType="disc" fontSize="sm" color="gray.700" lineHeight="1.65">
                    {sec.observations.map((point, pIdx) => (
                      <ListItem key={pIdx}>{point}</ListItem>
                    ))}
                  </List>
                </Box>

                {/* Potential Actions to Evaluate */}
                <Box
                  bg="blue.50"
                  borderLeft="4px solid"
                  borderLeftColor="blue.400"
                  p={4}
                  borderRadius="md"
                  mb={4}
                >
                  <Text fontWeight="bold" fontSize="sm" color="blue.900" mb={2}>
                    Potential Actions to Evaluate:
                  </Text>
                  <List spacing={2} pl={5} styleType="disc" fontSize="sm" color="blue.900" lineHeight="1.65">
                    {sec.potentialActions.map((action, aIdx) => (
                      <ListItem key={aIdx}>{action}</ListItem>
                    ))}
                  </List>
                </Box>

                {/* Related Pages */}
                <HStack spacing={2} wrap="wrap">
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mr={1}>
                    Related Pages:
                  </Text>
                  {sec.links.map((lnk, lIdx) => (
                    <Button
                      key={lIdx}
                      size="xs"
                      colorScheme={lnk.color}
                      variant="outline"
                      rightIcon={<ExternalLinkIcon />}
                      onClick={() => navigate(lnk.path)}
                    >
                      {lnk.label}
                    </Button>
                  ))}
                </HStack>

                {idx < edition.sections.length - 1 && <Divider mt={8} />}
              </Box>
            ))}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default NMAMetaAnalysisReport;
